// VideoThumb.tsx — the thumbnail every Videos card draws (2026-09-26).
// A real video shows its uploaded poster frame; a seed item draws generated art and wears a
// "Sample" badge (owner: keep samples, clearly labelled, until real videos exist). The
// duration pill sits in the corner either way. Render inside a position:relative box.
// 1.27.60: a thin bar along the bottom shows how much of it you have watched.
import styled from 'styled-components';
import { GeneratedThumb } from '@/components/GeneratedThumb/GeneratedThumb';
import { ThumbBadge } from '@/components/GeneratedThumb/ThumbBadge';
import type { VideoItem } from '@/core/models/video';
import { formatDuration } from './meta';

const Poster = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
`;

const Progress = styled.span`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  background: rgba(255, 255, 255, 0.28);
  > span {
    display: block;
    height: 100%;
    background: ${({ theme }) => theme.color.accentCyan};
  }
`;

export interface VideoThumbProps {
  video: Pick<VideoItem, 'id' | 'durationSec' | 'posterUrl' | 'sample' | 'progress'>;
  compact?: boolean;
  /** Eager-load (the hero). */
  priority?: boolean;
}

export function VideoThumb({ video, compact = false, priority = false }: VideoThumbProps) {
  return (
    <>
      {video.posterUrl ? (
        <Poster
          src={video.posterUrl}
          alt=""
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          data-testid="video-poster"
        />
      ) : (
        <GeneratedThumb seedKey={video.id} fill />
      )}
      {video.sample && (
        <ThumbBadge $corner="tl" $compact={compact} data-testid="sample-badge">
          Sample
        </ThumbBadge>
      )}
      <ThumbBadge $compact={compact}>{formatDuration(video.durationSec)}</ThumbBadge>
      {video.progress !== undefined && video.progress > 0 && (
        <Progress data-testid="watch-progress" aria-hidden="true">
          <span style={{ width: `${Math.round(video.progress * 100)}%` }} />
        </Progress>
      )}
    </>
  );
}
